import { Component, inject, Input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { INotification } from '../../models/notification';
import { SharedModalUtils } from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { selectUserAccessStoreAccess } from '../../../user-access/user-access-store/user-access.selectors';
import { StockTakeRetryProcessingFlowService } from '../../../stock-take/services/stock-take-retry-processing-flow.service';
import {
  buildStockTakeProcessingErrorDescription,
  isStockTakeIqApiNotSupportedError,
  parseStockTakeFailedItemCodes,
  parseStockTakeFailedItems,
  stockTakeIqApiNotSupportedSummary,
} from '../../../stock-take/utils/stock-take-processing-error.utils';
import { updateNotification } from '../../store/notification.actions';
import { NotificationService } from '../../service/notification.service';
import { NotificationTypeEnum } from '../../models/notification-types.enum';

/**
 * Displays the detail panel for a STOCK_TAKE type notification.
 * Shows the stock take ID; full backend message opens in a modal on demand.
 */
@Component({
  selector: 'app-notification-type-stock-take',
  templateUrl: './notification-type-stock-take.component.html',
  styleUrls: ['./notification-type-stock-take.component.scss'],
  standalone: false,
})
export class NotificationTypeStockTakeComponent {
  private readonly store = inject(Store);
  private readonly sharedModalUtils = inject(SharedModalUtils);
  private readonly retryProcessingFlow = inject(StockTakeRetryProcessingFlowService);
  private readonly notificationService = inject(NotificationService);

  private readonly userStoreAccess = toSignal(
    this.store.select(selectUserAccessStoreAccess),
    { initialValue: {} as Record<string, number> },
  );

  notification: INotification;

  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;

  protected readonly iqNotSupportedSummary = stockTakeIqApiNotSupportedSummary();

  /**
   * IQ is too old to expose the stock take submission API — retry will not help.
   */
  protected get isIqVersionNotSupported(): boolean {
    return isStockTakeIqApiNotSupportedError(this.notification?.response);
  }

  /**
   * Failed stock take notifications can be re-queued once for backend processing.
   */
  protected get canRetryProcessing(): boolean {
    return (
      !!this.notification &&
      !this.notification.success &&
      !this.notification.retried &&
      !this.isIqVersionNotSupported &&
      !!this.notification.event
    );
  }

  protected get failedItemCodes(): string[] {
    return parseStockTakeFailedItemCodes(this.notification?.response, this.notification?.itemErrors);
  }

  protected get canRetryWithoutFailedItems(): boolean {
    return this.canRetryProcessing && this.failedItemCodes.length > 0;
  }

  @Input() set setNotification(notification: INotification) {
    this.notification = notification;
  }

  /**
   * Open a modal with the full notification response from the server.
   */
  protected async onViewMessage(): Promise<void> {
    const text = this.notification?.response;
    if (!text) return;

    const isIqError = this.isIqVersionNotSupported;
    await this.sharedModalUtils.createModal({
      modalTitle: isIqError ? 'IQ upgrade required' : 'Stock take message',
      modalTitleIcon: isIqError ? IconsEnum.infoSlabCircleOutline : IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: isIqError ? IconColourEnum.yellow : IconColourEnum.primary,
      contextDescription: buildStockTakeProcessingErrorDescription(text),
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Close',
      cssClass: ['shared-basic-modal-css-medium'],
    });
  }

  /**
   * Re-queue failed stock take processing after manager confirmation and final-figures review.
   * When IQ rejected items, asks to remove those items (with reasons) first.
   */
  protected async onRetryProcessing(): Promise<void> {
    if (!this.canRetryProcessing) {
      return;
    }

    const storeId = this.notification.storeId;
    const adjustmentId = this.notification.event;
    const storeAccessCode = this.userStoreAccess()[storeId] ?? 0;
    const excludeFailedItems = parseStockTakeFailedItems(
      this.notification.response,
      this.notification.itemErrors,
    );
    const didRetry = await this.retryProcessingFlow.runRetryFlow(
      storeId,
      adjustmentId,
      storeAccessCode,
      {
        errorMsg: this.notification.response,
        itemErrors: this.notification.itemErrors,
        excludeFailedItems: excludeFailedItems.length > 0 ? excludeFailedItems : undefined,
      },
    );
    this.markNotificationRetried(didRetry);
  }

  /**
   * Remove IQ-rejected item codes from the stock take, then review remaining figures and re-queue processing.
   */
  protected async onRetryWithoutFailedItems(): Promise<void> {
    if (!this.canRetryWithoutFailedItems) {
      return;
    }

    const storeId = this.notification.storeId;
    const adjustmentId = this.notification.event;
    const storeAccessCode = this.userStoreAccess()[storeId] ?? 0;
    const excludeFailedItems = parseStockTakeFailedItems(
      this.notification.response,
      this.notification.itemErrors,
    );
    const didRetry = await this.retryProcessingFlow.runRetryFlow(
      storeId,
      adjustmentId,
      storeAccessCode,
      {
        errorMsg: this.notification.response,
        itemErrors: this.notification.itemErrors,
        excludeFailedItems,
      },
    );
    this.markNotificationRetried(didRetry);
  }

  private markNotificationRetried(didRetry: boolean): void {
    if (!didRetry || this.notification.retried) {
      return;
    }

    const updatedNotification: INotification = {
      ...this.notification,
      retried: true,
    };
    this.notification = updatedNotification;
    this.store.dispatch(updateNotification({notification: updatedNotification}));

    // Ensure Firestore is updated even if NgRx effect payload is incomplete.
    void this.notificationService
      .markNotificationsRetriedByEventId(this.notification.event, {
        storeId: this.notification.storeId,
        type: NotificationTypeEnum.STOCK_TAKE,
      })
      .catch((error: unknown) => {
        console.error(
          '[StockTake] Failed to mark notification retried by event id:',
          this.notification.event,
          error,
        );
      });
  }
}
